import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import {
  getADOTReplenishmentInputsDisabled,
  getADOTReplenishmentTotals,
  toADOTBaseReplenishment,
} from '@atom/components/common/requests/adotReplenishmentUtilities';
import { CREATE_REPLENISHMENT } from '@atom/graph/assetRequest';
import { Snackbar } from '@atom/mui';
import { ReplenishmentCreateInput } from '@atom/types/assetRequest';

import ViewRequestContext from '../ViewRequestContext';

import ADOTReplenishmentForm from './ADOTReplenishmentForm';

type ADOTAddReplenishmentViewProps = {
  onCancel: () => void;
};

const ADOTAddReplenishmentView = ({
  onCancel,
}: ADOTAddReplenishmentViewProps) => {
  const {
    activeRequest,
    handleGetActiveRequest,
    onAssetRefresh,
    onReplenishmentSaved,
  } = useContext(ViewRequestContext);

  const [inputs, setInputs] = useState<ReplenishmentCreateInput>();
  const [saving, setSaving] = useState<boolean>(false);

  const [createReplenishment] = useMutation(CREATE_REPLENISHMENT);

  const { purchaseUnit, orderedBase, receivedBase } =
    getADOTReplenishmentTotals(activeRequest);

  const updateInput = (
    value: any,
    property: keyof ReplenishmentCreateInput,
  ) => {
    setInputs(prev => ({ ...prev, [property]: value }));
  };

  const onCreate = async () => {
    setSaving(true);

    try {
      await createReplenishment({
        variables: {
          input: {
            id: activeRequest.id,
            ...toADOTBaseReplenishment(inputs, purchaseUnit),
          },
        },
      });

      Snackbar.info({ message: 'Replenishment created.' });

      if (onReplenishmentSaved) {
        onReplenishmentSaved(activeRequest);
        return;
      }

      handleGetActiveRequest();
      onCancel();
      onAssetRefresh?.();
    } catch (error) {
      Snackbar.error({ message: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ADOTReplenishmentForm
      title="Create Replenishment"
      purchaseUnit={purchaseUnit}
      totalOrderedBase={orderedBase}
      totalReceivedBase={receivedBase}
      inputs={inputs}
      updateInput={updateInput}
      onConfirm={onCreate}
      onCancel={onCancel}
      confirmText="Create"
      confirmDisabled={getADOTReplenishmentInputsDisabled(inputs) || saving}
      dataCyPrefix="adotReplenishmentAdd"
    />
  );
};

export default ADOTAddReplenishmentView;
